/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなくテーブルセルの render 関数。
 * MasterDataTable は戻り値を <TableCell> の children として表示するだけで独立した component subtree を
 * 持たないため、lint 警告の前提 (= subtree 破棄) に該当しない (CisContractCsvUpload と同判断)。
 */
import { useRef, useState } from 'react'

import { Code, ConnectError } from '@connectrpc/connect'
import {
  type ImportCustomerContractsRowResult,
  ImportCustomerContractsRowStatus,
} from '@ebalance/proto/bffuser/v1/customer_contract_csv_model_pb'
import { type ImportCustomerContractsResponse } from '@ebalance/proto/bffuser/v1/customer_contract_csv_service_pb'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  Alert,
  Box,
  Flex,
  HStack,
  PrimaryButton,
  Spinner,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaArrowLeftRegular } from '@enechain/ecloud-icons'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { CsvDiffErrorText } from '~/components/common/CsvUpload/CsvDiffTable'
import { CsvUploadNoopBanner } from '~/components/common/CsvUpload/CsvUploadNoopBanner'
import { CsvUploadStep1Panel } from '~/components/common/CsvUpload/CsvUploadStep1Panel'
import { Dropzone } from '~/components/common/CsvUpload/Dropzone'
import { Stepper } from '~/components/common/CsvUpload/Stepper'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { MAX_CSV_FILE_SIZE_BYTES } from '~/constants/csvUpload'
import { iconSize } from '~/constants/iconSize'
import { useCsvImportReturn } from '~/hooks/useCsvImportReturn'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { downloadCsv } from '~/libs/downloadCsv'
import { convertAreaLabel } from '~/models/area'
import { now as serverNow } from '~/models/clock'
import { decodeCsvFile } from '~/models/csvRecords'
import { resolveCsvUploadConfirmGate } from '~/models/csvUploadConfirmGate'
import { formatJstTimeHm } from '~/models/dateTime'
import {
  type SummaryCardFilter,
  SummaryCards,
} from '~/pages/CustomerContractCsvUpload/components/SummaryCards'
import { useImportCustomerContracts } from '~/pages/CustomerContractCsvUpload/hooks/useImportCustomerContracts'
import {
  type PrecheckErrorKey,
  precheckCustomerContractCsv,
} from '~/pages/CustomerContractCsvUpload/models/customerContractCsv'
import {
  CUSTOMER_CONTRACT_CSV_COLUMN_GUIDE,
  CUSTOMER_CONTRACT_CSV_SAMPLE_ROW,
  buildCustomerContractCsvTemplate,
} from '~/pages/CustomerContractCsvUpload/models/customerContractCsvColumnGuide'
import {
  PREVIEW_DISPLAY_LIMIT,
  filterRows,
  hasKwPeriods,
  kwPeriodsText,
  periodText,
  privateGenerationText,
  selectPreviewDisplayRows,
  stageLabel,
  transitionText,
  voltageLabel,
} from '~/pages/CustomerContractCsvUpload/models/previewRows'
import { routePaths } from '~/routes/routePaths'

/**
 * 雛形のファイル名。命名規則は `{データ種別名}_テンプレート.csv` (日付なし)、データ種別名は common.md
 * 第6節「ダウンロード」の日本語データ種別名対応表 (`顧客契約`)。英語表示時の雛形名は英語表示ごと P1 のため
 * 日本語名だけを持つ (`CisContractCsvUpload` の `TEMPLATE_FILE_NAME` と同判断)。
 */
const TEMPLATE_FILE_NAME = '顧客契約_テンプレート.csv'

type FileInfo = {
  name: string
  size: number
  selectedAt: Date
  /** RPC に送る CSV 生バイト (backend がパースする)。 */
  content: Uint8Array
}

/** バイト数を "38 KB" のような概算表示に変換する。 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

/**
 * サーバー起因の失敗を画面に出す文言へ変換する。`ConnectError.message` は `[code] <文言>` の形に
 * 組み立てられるので、前置の無い `rawMessage` を使う。InvalidArgument (ヘッダー違反・文字コード) と
 * Aborted (確定時の競合「もう一度プレビューからやり直してください」) だけを、backend がユーザー向けに
 * 組み立てた「そのまま見せてよい文言」として扱い、それ以外は定型文に寄せる。
 */
export const serverErrorText = (
  e: unknown,
  t: (key: string) => string,
): string => {
  if (
    e instanceof ConnectError &&
    (e.code === Code.InvalidArgument || e.code === Code.Aborted)
  ) {
    return e.rawMessage
  }
  return t('page:customerContractCsvUpload.serverError')
}

/**
 * Step1 の前段チェックのエラーを i18n キーにする。読み込み自体の失敗は文字コードの問題では
 * ないので、DropZone と同じ汎用の文言にする。それ以外はこの画面の文言。
 */
const precheckMessageKey = (key: PrecheckErrorKey): string =>
  key === 'readFailed'
    ? 'common:upload.error.generic'
    : `page:customerContractCsvUpload.step1.precheck.${key}`

const statusLabel = (
  status: ImportCustomerContractsRowStatus,
  t: (key: string) => string,
): string => {
  switch (status) {
    case ImportCustomerContractsRowStatus.NEW:
      return t('page:customerContractCsvUpload.step2.status.new')
    case ImportCustomerContractsRowStatus.CHANGED:
      return t('page:customerContractCsvUpload.step2.status.changed')
    case ImportCustomerContractsRowStatus.UNCHANGED:
      return t('page:customerContractCsvUpload.step2.status.unchanged')
    case ImportCustomerContractsRowStatus.ERROR:
      return t('page:customerContractCsvUpload.step2.status.error')
    default:
      return ''
  }
}

/**
 * 顧客契約 CSV アップロード画面 (spec `docs/specs/admin/data/uploads/customer-contract.md`)。
 *
 * システム管理 > CSVアップロード 配下の 2 ステップウィザード (Step1 ファイル選択 → Step2 差分プレビュー →
 * 確定モーダル)。CSV のパース・厳格モード検証・差分判定 (新規 / 変更 / 据え置き) は
 * ImportCustomerContracts RPC (backend) が行い、このページは生バイトを送ってサーバー計算値をそのまま
 * 表示する (鉄則1 / frontend.md)。frontend 側は Step1 の軽い前段チェックのみ持つ。
 */
export const CustomerContractCsvUploadPage = (): React.ReactElement => {
  const showSuccessToast = useSuccessToast()
  const { t } = useTranslation()
  const importMutation = useImportCustomerContracts()
  const navigate = useNavigate()
  const csvReturn = useCsvImportReturn('customerContract')

  const [step, setStep] = useState<1 | 2>(1)
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null)
  const [precheckError, setPrecheckError] = useState<
    | { key: PrecheckErrorKey; params?: Record<string, number | string> }
    | undefined
  >(undefined)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [previewResult, setPreviewResult] =
    useState<ImportCustomerContractsResponse | null>(null)
  const [serverErrorMessage, setServerErrorMessage] = useState<
    string | undefined
  >(undefined)
  const [confirmErrorMessage, setConfirmErrorMessage] = useState<
    string | undefined
  >(undefined)
  const [activeFilter, setActiveFilter] = useState<SummaryCardFilter>('all')
  // dry_run / 確定リクエストの世代番号。通信中に Step1 へ戻って別 CSV を選び直した場合、遅延して届いた
  // 古い応答を反映すると「表示は CSV A の差分・確定対象は CSV B」に食い違う。ファイル選択・戻る・
  // リセットで世代を進め、一致する応答のみ反映する。
  const previewGenerationRef = useRef(0)
  // ファイル読込は非同期のため、読込中に別ファイルを選び直すと古い読込結果が後着して最新選択を上書きしうる。
  const readSeqRef = useRef(0)

  const summary = previewResult?.summary
  const hasRowError = (summary?.errorRowCount ?? 0) > 0
  const confirmGate = resolveCsvUploadConfirmGate(
    summary === undefined ? undefined : { ...summary, deletedRowCount: 0 },
    hasRowError,
  )
  const canOpenConfirm = !isPreviewLoading && confirmGate.canOpenConfirm

  const resetToStep1 = (): void => {
    previewGenerationRef.current += 1
    setStep(1)
    setFileInfo(null)
    setPrecheckError(undefined)
    setPreviewResult(null)
    setServerErrorMessage(undefined)
    setConfirmErrorMessage(undefined)
    setActiveFilter('all')
  }

  const handleFileSelected = (file: File): void => {
    previewGenerationRef.current += 1
    // 世代番号は「選択された」時点で必ず進める (拒否する選択でも進めないと、先行読込が完了した時に
    // 旧ファイルの fileInfo が復活して旧ファイルを確定できてしまう)。
    const seq = ++readSeqRef.current
    setFileInfo(null)
    setPrecheckError(undefined)
    setPreviewResult(null)
    setConfirmErrorMessage(undefined)
    // サイズ上限は読み込み**前**に見る (arrayBuffer() は全体をメモリに載せる)。
    if (file.size > MAX_CSV_FILE_SIZE_BYTES) {
      setPrecheckError({ key: 'fileSizeExceeded' })
      return
    }
    // common.md §1「文字コードの判定」: UTF-8 → Shift_JIS の順で判定し、判定した文字コードで
    // 読んだ値で前段チェックを行う。どちらでも読めない時は §4 の文言で Step1 に止める。
    void decodeCsvFile(file).then((decoded) => {
      if (seq !== readSeqRef.current) return
      if (decoded.kind === 'read') {
        setPrecheckError({ key: 'readFailed' })
        return
      }
      if (decoded.kind === 'encoding') {
        setPrecheckError({ key: 'notUtf8' })
        return
      }
      const result = precheckCustomerContractCsv(decoded.text)
      if (!result.ok) {
        setPrecheckError({ key: result.messageKey, params: result.params })
        return
      }
      setFileInfo({
        name: file.name,
        size: file.size,
        selectedAt: serverNow(),
        content: decoded.bytes,
      })
    })
  }

  /** 雛形 DL: ヘッダー行 + 記入例 3 行 (common.md AC-DUC-031)。 */
  const handleTemplateDownload = (): void => {
    downloadCsv(TEMPLATE_FILE_NAME, buildCustomerContractCsvTemplate())
  }

  const runPreview = (): void => {
    if (fileInfo === null) return
    const generation = previewGenerationRef.current
    setStep(2)
    setIsPreviewLoading(true)
    setServerErrorMessage(undefined)
    setActiveFilter('all')
    importMutation
      .mutateAsync({
        content: fileInfo.content,
        dryRun: true,
        csvFileName: fileInfo.name,
      })
      .then((resp) => {
        if (generation !== previewGenerationRef.current) return // 古い応答は破棄
        setPreviewResult(resp)
        setIsPreviewLoading(false)
      })
      .catch((e: unknown) => {
        if (generation !== previewGenerationRef.current) return
        // ヘッダー違反・文字コードは backend が InvalidArgument で返す (ファイル全体の拒否)
        setServerErrorMessage(serverErrorText(e, t))
        setIsPreviewLoading(false)
      })
  }

  const handleBackToStep1 = (): void => {
    previewGenerationRef.current += 1
    setStep(1)
    setPreviewResult(null)
    setIsPreviewLoading(false)
  }

  const handleCommit = (): void => {
    if (fileInfo === null) return
    setConfirmErrorMessage(undefined)
    const generation = previewGenerationRef.current
    importMutation
      .mutateAsync({
        content: fileInfo.content,
        dryRun: false,
        csvFileName: fileInfo.name,
      })
      .then((resp) => {
        const isCurrent = generation === previewGenerationRef.current
        if (resp.applied) {
          // 反映は完了しているので、画面を離れていても必ず知らせる (黙って捨てると再投入しかねない)
          showSuccessToast({
            title: t('page:customerContractCsvUpload.successToast'),
          })
          if (isCurrent) {
            setShowConfirmModal(false)
            resetToStep1()
          }
          return
        }
        // 確定時再検証で新たな行エラー → 反映せず Step2 のエラー表示 (古い応答は破棄)
        if (!isCurrent) return
        setShowConfirmModal(false)
        setPreviewResult(resp)
      })
      .catch((e: unknown) => {
        if (generation !== previewGenerationRef.current) return
        const message = serverErrorText(e, t)
        // ファイル全体の拒否は同じ CSV の再送では解消しない。通信・サーバー失敗はモーダルに残して再試行できる
        if (e instanceof ConnectError && e.code === Code.InvalidArgument) {
          setShowConfirmModal(false)
          setServerErrorMessage(message)
        } else if (e instanceof ConnectError && e.code === Code.Aborted) {
          // 競合: 見ていたプレビューは古い。モーダルを閉じてプレビューも捨て (確定ボタンが無効になる)、
          // 「戻る → 内容を確認する」でプレビューし直させる。同じプレビューのまま再確定はさせない
          setShowConfirmModal(false)
          setPreviewResult(null)
          setServerErrorMessage(message)
        } else {
          setConfirmErrorMessage(message)
        }
      })
  }

  const allRows = previewResult?.rowResults ?? []
  const filteredRows = filterRows(allRows, activeFilter)

  // アップロード済みファイル情報 (ファイル名 / サイズ / 選択時刻)。Step1 / Step2 の両方に常時表示する
  // (common.md AC-DUC-005 / AC-DUC-006)。
  const fileInfoBox = fileInfo !== null && (
    <Flex
      alignItems="center"
      gap={3}
      marginTop={3}
      padding={3}
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="md"
    >
      <Box>
        <Text fontWeight="bold">{fileInfo.name}</Text>
        <Text color="Text.midEmphasis" fontSize="sm">
          {formatFileSize(fileInfo.size)} ・{' '}
          {t('page:customerContractCsvUpload.fileInfo.selectedAt', {
            time: formatJstTimeHm(fileInfo.selectedAt),
          })}
        </Text>
      </Box>
    </Flex>
  )

  const unset = t('page:customerContractCsvUpload.step2.unsetPlaceholder')
  const numberText = (value: string | undefined): string =>
    value ?? t('page:customerContractCsvUpload.step2.numberUndecided')

  const columns: Array<
    MasterDataTableColumn<ImportCustomerContractsRowResult>
  > = [
    // ステータスは全画面共通で列の先頭に置く
    {
      key: 'status',
      header: t('page:customerContractCsvUpload.step2.columns.status'),
      cell: (row) => (
        <Text
          as="span"
          color={
            row.status === ImportCustomerContractsRowStatus.ERROR
              ? 'Text.negativeHigh'
              : undefined
          }
        >
          {statusLabel(row.status, t)}
        </Text>
      ),
    },
    {
      key: 'rowNumber',
      header: t('page:customerContractCsvUpload.step2.columns.rowNumber'),
      align: 'right',
      cell: (row) => row.rowNumber,
    },
    {
      key: 'stage',
      header: t('page:customerContractCsvUpload.step2.columns.stage'),
      // エラーの行も、変更の行は登録済みの区分を出す (新規の行のエラーは区分が決まらず空)
      cell: (row) =>
        transitionText(
          row,
          row.previousContractStage === undefined
            ? ''
            : stageLabel(t, row.previousContractStage),
          stageLabel(t, row.contractStage),
          unset,
        ),
    },
    {
      key: 'customer',
      header: t('page:customerContractCsvUpload.step2.columns.customer'),
      cell: (row) =>
        row.isNewCustomer
          ? t('page:customerContractCsvUpload.step2.newCustomer', {
              name: row.customerName,
            })
          : transitionText(
              row,
              row.existingCustomerName ?? row.customerName,
              row.customerName,
              unset,
            ),
    },
    {
      key: 'contractor',
      header: t('page:customerContractCsvUpload.step2.columns.contractor'),
      cell: (row) => {
        if (row.contractorName === undefined) return ''
        if (row.existingContractorName !== undefined) {
          return transitionText(
            row,
            row.existingContractorName,
            row.contractorName,
            unset,
          )
        }
        return row.isNewContractor
          ? t('page:customerContractCsvUpload.step2.newContractor', {
              name: row.contractorName,
            })
          : row.contractorName
      },
    },
    {
      key: 'area',
      header: t('page:customerContractCsvUpload.step2.columns.area'),
      cell: (row) =>
        transitionText(
          row,
          convertAreaLabel(row.existingAreaCode ?? row.areaCode),
          convertAreaLabel(row.areaCode),
          unset,
        ),
    },
    {
      key: 'voltageClass',
      header: t('page:customerContractCsvUpload.step2.columns.voltageClass'),
      cell: (row) =>
        transitionText(
          row,
          voltageLabel(t, row.existingVoltageClass ?? row.voltageClass),
          voltageLabel(t, row.voltageClass),
          unset,
        ),
    },
    {
      key: 'number',
      header: t('page:customerContractCsvUpload.step2.columns.number'),
      cell: (row) =>
        row.status === ImportCustomerContractsRowStatus.ERROR
          ? (row.supplyPointSpecificNumber ?? '')
          : transitionText(
              row,
              numberText(row.existingSupplyPointSpecificNumber),
              numberText(row.supplyPointSpecificNumber),
              unset,
            ),
    },
    {
      key: 'contractKw',
      header: t('page:customerContractCsvUpload.step2.columns.contractKw'),
      align: 'right',
      cell: (row) =>
        transitionText(
          row,
          row.existingContractKw?.value ?? '',
          row.contractKw?.value ?? '',
          unset,
        ),
    },
    // 契約電力の変更 (期間) を持つ行があるファイルだけ列を出す
    ...(hasKwPeriods(filteredRows)
      ? [
          {
            key: 'kwPeriods',
            header: t('page:customerContractCsvUpload.step2.columns.kwPeriods'),
            cell: (row: ImportCustomerContractsRowResult): string =>
              transitionText(
                row,
                kwPeriodsText(t, row.existingKwPeriods),
                kwPeriodsText(t, row.kwPeriods),
                unset,
              ),
          },
        ]
      : []),
    {
      key: 'period',
      header: t('page:customerContractCsvUpload.step2.columns.period'),
      cell: (row) =>
        row.supplyStartDate === undefined
          ? ''
          : transitionText(
              row,
              row.existingSupplyStartDate === undefined
                ? ''
                : periodText(
                    row.existingSupplyStartDate,
                    row.existingSupplyEndDate,
                  ),
              periodText(row.supplyStartDate, row.supplyEndDate),
              unset,
            ),
    },
    {
      key: 'retailer',
      header: t('page:customerContractCsvUpload.step2.columns.retailer'),
      cell: (row) =>
        transitionText(
          row,
          row.existingRetailerBusinessCode ?? '',
          row.retailerBusinessCode ?? '',
          unset,
        ),
    },
    {
      key: 'privateGeneration',
      header: t(
        'page:customerContractCsvUpload.step2.columns.privateGeneration',
      ),
      cell: (row) =>
        row.status === ImportCustomerContractsRowStatus.ERROR &&
        row.customerSupplyPointId === undefined
          ? ''
          : transitionText(
              row,
              row.existingPrivateGeneration === undefined
                ? ''
                : privateGenerationText(t, row.existingPrivateGeneration),
              privateGenerationText(t, row.privateGeneration),
              unset,
            ),
    },
    // 備考列: 行のエラー内容 (`CisContractCsvUpload` の備考列と同じ。行番号は行番号の列にある)
    {
      key: 'note',
      header: t('page:customerContractCsvUpload.step2.columns.note'),
      cell: (row) =>
        row.errorMessages.length > 0 ? (
          <CsvDiffErrorText>{row.errorMessages.join(' / ')}</CsvDiffErrorText>
        ) : (
          ''
        ),
    },
  ]

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('page:customerContractCsvUpload.breadcrumb.admin'),
          to: routePaths.adminHub.path,
        },
        {
          label: t('page:customerContractCsvUpload.breadcrumb.uploads'),
          to: routePaths.adminUploads.path,
        },
        {
          label: t('page:customerContractCsvUpload.breadcrumb.title'),
          bold: true,
        },
      ]}
    >
      {/* spec `admin/data/uploads/index.md`: 個別アップロード画面のタイトル下補足も本表の説明文と一致させる。
          取込ハブのカードと同じ i18n キーを共有する。見出しは渡さない (パンくず末尾が画面名を兼ねる)。 */}
      <PageIntro
        description={t('page:adminUploads.types.customerContract.desc')}
      />
      <Box marginTop={4}>
        <Stepper
          currentStep={step}
          stepLabels={[
            t('page:customerContractCsvUpload.stepper.step1'),
            t('page:customerContractCsvUpload.stepper.step2'),
          ]}
        />

        {step === 1 && (
          <Box marginTop={4}>
            <CsvUploadStep1Panel
              downloads={[{ onClick: handleTemplateDownload }]}
              guideSections={[{ rows: CUSTOMER_CONTRACT_CSV_COLUMN_GUIDE }]}
              sampleRow={CUSTOMER_CONTRACT_CSV_SAMPLE_ROW}
              selectHint={t('page:customerContractCsvUpload.step1.hint')}
            >
              <Dropzone
                onFileSelected={handleFileSelected}
                specText={t('page:customerContractCsvUpload.step1.spec')}
              />

              {precheckError !== undefined && (
                <Alert marginTop={3} status="error">
                  <Box>
                    <Text fontWeight="bold">
                      {t(
                        'page:customerContractCsvUpload.step1.syntaxErrorTitle',
                      )}
                    </Text>
                    <Text fontSize="sm">
                      {t(
                        precheckMessageKey(precheckError.key),
                        precheckError.params,
                      )}
                    </Text>
                  </Box>
                </Alert>
              )}

              {fileInfoBox}

              <HStack justify="flex-end" marginTop={4}>
                {/* design (`admin/uploads/*.html` `#btn-cancel-step1`) どおり。行き先は取込を始めた画面
                    (returnTo)、無ければ CSV 入出力の一覧 (`CisContractCsvUpload` と同じ) */}
                <TertiaryButton
                  onClick={() => {
                    void navigate(csvReturn.cancelPath)
                  }}
                  size="sm"
                >
                  {t('common:action.cancel')}
                </TertiaryButton>
                {fileInfo !== null && (
                  <PrimaryButton onClick={runPreview} size="sm">
                    {t('page:customerContractCsvUpload.step1.toStep2')}
                  </PrimaryButton>
                )}
              </HStack>
            </CsvUploadStep1Panel>
          </Box>
        )}

        {step === 2 && (
          <Box marginTop={4}>
            <HStack justify="space-between" marginBottom={3}>
              <TertiaryButton onClick={handleBackToStep1} size="sm">
                <FaArrowLeftRegular boxSize={iconSize.labeledButton} />
                {t('page:customerContractCsvUpload.step2.backToStep1')}
              </TertiaryButton>
              <PrimaryButton
                disabled={!canOpenConfirm}
                onClick={() => setShowConfirmModal(true)}
                size="sm"
              >
                {t('page:customerContractCsvUpload.step2.confirmOpen')}
              </PrimaryButton>
            </HStack>

            <Text textStyle="sectionTitle" marginBottom={1}>
              {t('page:customerContractCsvUpload.step2.title')}
            </Text>
            <Text marginBottom={3} color="Text.midEmphasis" fontSize="sm">
              {t('page:customerContractCsvUpload.step2.hint')}
            </Text>

            <Box marginTop={-1} marginBottom={3}>
              {fileInfoBox}
            </Box>

            {isPreviewLoading && (
              <Flex justifyContent="center" paddingY={8} role="progressbar">
                <Spinner />
                <Text marginLeft={3}>
                  {t('page:customerContractCsvUpload.step2.loading')}
                </Text>
              </Flex>
            )}

            {serverErrorMessage !== undefined && (
              <Alert marginBottom={3} status="error">
                {serverErrorMessage}
              </Alert>
            )}

            {!isPreviewLoading && summary !== undefined && (
              <SummaryCards
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                summary={summary}
              />
            )}

            {!isPreviewLoading && hasRowError && (
              <Alert marginBottom={3} status="error">
                {t('page:customerContractCsvUpload.step2.errorBannerDesc')}
              </Alert>
            )}

            {!isPreviewLoading &&
              confirmGate.showNoRowsToProcessBanner &&
              summary !== undefined && (
                <CsvUploadNoopBanner
                  description={t(
                    'page:customerContractCsvUpload.step2.noopBannerDesc',
                    { count: summary.unchangedRowCount },
                  )}
                />
              )}

            {!isPreviewLoading &&
              previewResult !== null &&
              filteredRows.length > PREVIEW_DISPLAY_LIMIT && (
                <Alert marginBottom={3} status="info">
                  {t('page:customerContractCsvUpload.step2.displayLimitNote', {
                    total: filteredRows.length,
                    limit: PREVIEW_DISPLAY_LIMIT,
                  })}
                </Alert>
              )}

            {/* 据え置きは row_results に載らない (レスポンスサイズ対策) ため、
                「据え置き」フィルタ選択時は件数のみである旨を明示する。 */}
            {!isPreviewLoading &&
              activeFilter === 'unchanged' &&
              (summary?.unchangedRowCount ?? 0) > 0 && (
                <Alert marginBottom={3} status="info">
                  {t(
                    'page:customerContractCsvUpload.step2.unchangedNotListed',
                    {
                      count: summary?.unchangedRowCount ?? 0,
                    },
                  )}
                </Alert>
              )}

            {!isPreviewLoading && previewResult !== null && (
              <MasterDataTable
                columns={columns}
                getRowKey={(row) => String(row.rowNumber)}
                rows={selectPreviewDisplayRows(filteredRows)}
              />
            )}
          </Box>
        )}

        <ConfirmDialog
          body={
            <Box>
              {confirmErrorMessage !== undefined && (
                <Alert marginBottom={3} status="error">
                  {confirmErrorMessage}
                </Alert>
              )}
              <Text marginBottom={2}>
                {t('page:customerContractCsvUpload.confirmModal.body')}
              </Text>
              {fileInfo !== null && (
                <Text marginBottom={2} color="Text.midEmphasis" fontSize="sm">
                  {fileInfo.name}
                </Text>
              )}
              {summary !== undefined && (
                <Box
                  marginBottom={2}
                  padding={2}
                  borderWidth="1px"
                  borderColor="Border.lowEmphasis"
                  borderRadius="md"
                >
                  <Text fontSize="sm">
                    {t(
                      'page:customerContractCsvUpload.confirmModal.newRowCount',
                      { count: summary.newRowCount },
                    )}
                  </Text>
                  <Text fontSize="sm">
                    {t(
                      'page:customerContractCsvUpload.confirmModal.changedRowCount',
                      { count: summary.changedRowCount },
                    )}
                  </Text>
                  {summary.stageChangeRowCount > 0 && (
                    <Text color="Text.midEmphasis" fontSize="sm">
                      {t(
                        'page:customerContractCsvUpload.confirmModal.stageChangeRowCount',
                        { count: summary.stageChangeRowCount },
                      )}
                    </Text>
                  )}
                  <Text fontSize="sm">
                    {t(
                      'page:customerContractCsvUpload.confirmModal.unchangedRowCount',
                      { count: summary.unchangedRowCount },
                    )}
                  </Text>
                  <Text
                    textStyle="captionStrong"
                    marginTop={1}
                    paddingTop={1}
                    borderTopWidth="1px"
                    borderTopColor="Border.lowEmphasis"
                  >
                    {t(
                      'page:customerContractCsvUpload.confirmModal.processedTotal',
                      {
                        count:
                          summary.newRowCount +
                          summary.changedRowCount +
                          summary.unchangedRowCount,
                      },
                    )}
                  </Text>
                </Box>
              )}
            </Box>
          }
          confirmLabel={t('page:customerContractCsvUpload.confirmModal.commit')}
          isLoading={importMutation.isPending}
          isOpen={showConfirmModal}
          onCancel={() => {
            setConfirmErrorMessage(undefined)
            setShowConfirmModal(false)
          }}
          onConfirm={handleCommit}
          title={t('page:customerContractCsvUpload.confirmModal.title')}
        />
      </Box>
    </PageLayout>
  )
}
